// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.UiState.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { findIndex, isNil } from 'lodash/fp';
import { action, observable, computed } from 'mobx';
import settingConstant from 'logic/data/Izenda.SettingConstants';
import AccountInfo from 'logic/models/Izenda.AccountInfo';
import localizer from 'share/localization/Izenda.Localizer';
import { PROVISIONING_STATUS } from 'logic/data/Izenda.AppGlobalConstants';
import AuthUtils from 'share/utils/Izenda.AuthUtils';

class UiState {
  @observable detectChangeCount = 0;
  @observable detectChangeVisibleConnection = 0;
  @observable tenantId = null;
  @observable disableSystemSetting = false; // used for enable/disable Setting Level dropdown
  @observable onlyShowTenant = false; // used for enable/disable Tenant Level dropdown
  @observable checkDirtyFunction = null;
  @observable onOkDirtyForm = null;
  @observable systemError = null;
  @observable systemMode = settingConstant.SYSTEM_MODE;
  @observable isMobile = false;
  @observable needLogin = false;
  @observable currentUser = new AccountInfo();
  @observable provisioningStatus = PROVISIONING_STATUS.NOT_STARTED;
  @observable notificationMessages = [];
  @observable exportingLoadings = [];
  @observable hasSetupDatabase = true;
  @observable expiredLicense = false;
  defaultDirtyMessage = localizer.getText('COMMON_MESSAGES_CONFIRM_CHANGES_LOST');
  dirtyMessage = this.defaultDirtyMessage;

  @computed
  get isDirty() {
    return this.checkDirtyFunction && this.checkDirtyFunction();
  }
  @computed
  get hideSystemLevel() {
    return this.systemMode === settingConstant.SYSTEM_MODE || !this.currentUser.systemAdmin;
  }
  @computed
  get canProvisioning() {
    return (
      this.provisioningStatus === PROVISIONING_STATUS.NOT_STARTED ||
      this.provisioningStatus === PROVISIONING_STATUS.ERROR
    );
  }
  @computed
  get isMapDataAvailable() {
    return this.provisioningStatus === PROVISIONING_STATUS.SUCCESS;
  }
  @computed
  get isSingleTenantMode() {
    return this.systemMode === settingConstant.TENANT_MODE;
  }

  resetCheckDirtyFunction() {
    this.setCheckDirtyFunction(null, null);
  }

  setCheckDirtyFunction(checkDirtyFunction: Function, onOk: Function, dirtyMessage: string) {
    this.checkDirtyFunction = checkDirtyFunction;
    this.onOkDirtyForm = onOk;
    this.dirtyMessage = !isNil(dirtyMessage) ? dirtyMessage : this.defaultDirtyMessage;
  }

  getDetectChangeVisibleConnection() {
    return this.detectChangeVisibleConnection;
  }

  increaseDetectChangeVisibleConnection() {
    return this.detectChangeVisibleConnection++;
  }

  getDetectChangeCount() {
    return this.detectChangeCount;
  }

  increaseDetectChangeCount() {
    return this.detectChangeCount++;
  }

  getTenantId() {
    return this.tenantId;
  }

  @action('setTenantId')
  setTenantId(tenantId: string, keepState: boolean) {
    this.keepState = !!keepState;
    return (this.tenantId = tenantId);
  }

  setSystemError(error: string) {
    this.systemError = error;
  }

  resetSystemError() {
    this.systemError = null;
  }

  setMobileVersion(value: boolean) {
    this.isMobile = value;
  }

  getMobileVersion() {
    return this.isMobile;
  }

  /**
   * Disable system setting bar
   */
  updateDisableSystemSetting() {
    this.disableSystemSetting = true;
  }

  enableSystemSetting() {
    this.disableSystemSetting = false;
  }

  /**
   * Set disable setting level. Clear the tenantId when setting level is disabled
   */
  @action('setDisableSettingLevel')
  setDisableSettingLevel(disable: boolean) {
    this.disableSystemSetting = disable;
    if (disable) {
      this.tenantId = null;
      AuthUtils.removeSelectedTenant();
    }
  }

  /**
   * Update need login status.
   * @param  {boolean} value
   */
  setNeedLogin(value: boolean) {
    this.needLogin = value;
  }

  /**
   * Update current user detail.
   * @param  {Object} value
   */
  @action('setCurrentUser')
  setCurrentUser(value: any) {
    value = value || {};
    this.tenantId = value.tenantId;
    this.currentUser.populate(value);
    value.cultureName && localizer.setActiveLanguage(value.cultureName);
  }

  /**
   * Update Provisioning Status
   * @param value
   */
  setProvisioningStatus(value: number) {
    this.provisioningStatus = value;
  }

  /**
   * Add new Notification message
   * @param notification
   */
  addNotification(notification: any) {
    const index = findIndex({ key: notification.key }, this.notificationMessages);
    index === -1 && this.notificationMessages.unshift(notification);
  }

  removeNotification(key: string) {
    const index = findIndex({ key: key }, this.notificationMessages);
    index > -1 && this.notificationMessages.splice(index, 1);
  }

  /**
   * Reset exporting loadings.
   */
  resetExportingLoadings() {
    this.exportingLoadings.clear();
  }

  /**
   * Update expire license
   */
  updateLicenseStatus(expired: boolean) {
    this.expiredLicense = expired;
  }

  /**
   * Set system mode
   * @param {Number} systemMode
   */
  setSystemMode(systemMode: number) {
    this.systemMode = systemMode;
  }
}

const singleton = new UiState();
export default singleton;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.UiState.js